import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { MarkdownRenderer } from '@/components/markdown';
import {
  OFFICE_EMBED_LOAD_TIMEOUT_MS,
  OFFICE_EMBED_OVERLAY_MAX_MS,
} from '@/constants/workflow-editor';

interface BaseDisplayProps {
  data: string;
  className?: string;
}

interface JsonDisplayProps {
  data: unknown;
  className?: string;
}

interface MarkdownDisplayProps extends BaseDisplayProps {
  onContainerRef?: (ref: HTMLDivElement | null) => void;
}

export const TextDisplay: React.FC<BaseDisplayProps> = ({
  data,
  className,
}) => {
  return (
    <div className={`whitespace-pre-wrap break-words ${className || ''}`}>
      {data}
    </div>
  );
};

export const MarkdownDisplay: React.FC<MarkdownDisplayProps> = ({
  data,
  className,
  onContainerRef,
}) => {
  const markdownContainerRef = useRef<HTMLDivElement>(null);

  // Clean the data by removing wrapping brackets and quotes if they exist
  const cleanData = (() => {
    if (typeof data !== 'string') return data;

    // Remove wrapping [' and '] if they exist
    let cleaned = data.trim();
    if (
      (cleaned.startsWith('["') && cleaned.endsWith('"]')) ||
      (cleaned.startsWith("['") && cleaned.endsWith("']"))
    ) {
      cleaned = cleaned.slice(2, -2);
    }

    // Convert \n to proper markdown line breaks
    // Replace \n with two spaces + \n for hard line breaks in markdown
    cleaned = cleaned.replace(/\\n/g, '  \n');

    return cleaned;
  })();

  // Expose the container ref to parent
  React.useEffect(() => {
    if (onContainerRef) {
      onContainerRef(markdownContainerRef.current);
    }
  }, [onContainerRef]);

  return (
    <div style={{ minHeight: '600px' }}>
      <div ref={markdownContainerRef}>
        <MarkdownRenderer content={cleanData} className={className} />
      </div>
    </div>
  );
};

export function selectJsonDisplayData(
  value: unknown,
  structuredFallback: unknown,
): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    JSON.parse(value);
    return value;
  } catch {
    return structuredFallback !== null && typeof structuredFallback === 'object'
      ? structuredFallback
      : value;
  }
}

export const JsonDisplay: React.FC<JsonDisplayProps> = ({
  data,
  className,
}) => {
  let parsedData;
  try {
    parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  } catch {
    parsedData = { error: 'Invalid JSON', content: data };
  }

  return (
    <div className={`${className || ''}`}>
      <pre className='bg-muted p-3 rounded-md overflow-auto text-sm'>
        {JSON.stringify(parsedData, null, 2)}
      </pre>
    </div>
  );
};

export const ImageDisplay: React.FC<BaseDisplayProps> = ({
  data,
  className,
}) => {
  // Handle both base64 and URL data
  const imageSrc =
    data.startsWith('data:') || data.startsWith('http')
      ? data
      : `data:image/png;base64,${data}`;

  return (
    <div className={`${className || ''}`}>
      <img
        src={imageSrc}
        alt='Generated content'
        className='max-w-full h-auto rounded-md'
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};

export const FileDownloadDisplay: React.FC<BaseDisplayProps> = ({
  data,
  className,
}) => {
  const handleDownload = () => {
    // Handle file download logic
    const link = document.createElement('a');
    link.href = data;
    link.download = 'downloaded-file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`${className || ''}`}>
      <button
        onClick={handleDownload}
        className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
      >
        Download File
      </button>
    </div>
  );
};

export const OFFICE_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];

export const officeEmbedUrl = (fileUrl: string) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

const OfficeEmbedIframe: React.FC<BaseDisplayProps & { title: string }> =
  React.memo(({ data, className, title }) => {
    const embedUrl = useMemo(() => officeEmbedUrl(data), [data]);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showTimeoutHint, setShowTimeoutHint] = useState(false);

    useLayoutEffect(() => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      setIsLoading(true);
      setShowTimeoutHint(false);

      const onLoad = () => {
        setIsLoading(false);
        setShowTimeoutHint(false);
      };

      iframe.addEventListener('load', onLoad);
      iframe.src = embedUrl;

      const hintTimeoutId = window.setTimeout(() => {
        setShowTimeoutHint(true);
      }, OFFICE_EMBED_LOAD_TIMEOUT_MS);

      const maxTimeoutId = window.setTimeout(() => {
        setIsLoading(false);
      }, OFFICE_EMBED_OVERLAY_MAX_MS);

      return () => {
        iframe.removeEventListener('load', onLoad);
        window.clearTimeout(hintTimeoutId);
        window.clearTimeout(maxTimeoutId);
        iframe.src = 'about:blank';
      };
    }, [embedUrl]);

    return (
      <div
        className={`relative w-full h-full flex flex-col min-h-[600px] ${className || ''}`}
      >
        {isLoading ? (
          <div
            className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-muted-background px-4 text-sm text-foreground'
            aria-busy='true'
            aria-live='polite'
          >
            <Loader2
              className='h-8 w-8 shrink-0 animate-spin text-primary'
              aria-hidden
            />
            {showTimeoutHint ? (
              <p className='max-w-sm text-center text-muted-foreground'>
                This preview is taking longer than expected.{' '}
                <a
                  href={embedUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-medium text-primary underline underline-offset-4 hover:no-underline'
                >
                  Open in new tab
                </a>{' '}
                or{' '}
                <a
                  href={data}
                  download
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-medium text-primary underline underline-offset-4 hover:no-underline'
                >
                  download the file
                </a>{' '}
                to view.
              </p>
            ) : null}
          </div>
        ) : null}
        <iframe
          ref={iframeRef}
          width='100%'
          height='100%'
          style={{ flex: 1, border: 'none', minHeight: '600px' }}
          title={title}
        />
        <div className='flex items-start gap-2 px-3 py-2 border-t border-border text-xs text-muted-foreground bg-muted/40'>
          <Info className='size-3.5 shrink-0 mt-px' />
          <span>
            Preview uses Microsoft Office Online, which may occasionally be
            unavailable. Download the file to view it if there is an error.
          </span>
        </div>
      </div>
    );
  });

OfficeEmbedIframe.displayName = 'OfficeEmbedIframe';

export const PowerPointDocumentDisplay: React.FC<BaseDisplayProps> = (
  props,
) => <OfficeEmbedIframe {...props} title='PowerPoint Preview' />;

PowerPointDocumentDisplay.displayName = 'PowerPointDocumentDisplay';

export const WordDocumentDisplay: React.FC<BaseDisplayProps> = (props) => (
  <OfficeEmbedIframe {...props} title='Word Preview' />
);

WordDocumentDisplay.displayName = 'WordDocumentDisplay';

export const ExcelDocumentDisplay: React.FC<BaseDisplayProps> = (props) => (
  <OfficeEmbedIframe {...props} title='Excel Preview' />
);

ExcelDocumentDisplay.displayName = 'ExcelDocumentDisplay';
